import { useCallback, useEffect, useState } from 'react';

// ========== 型定義 ==========
export type NotificationType = 'habit' | 'relation' | 'event' | 'oshikatsu' | 'vip';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
  actionUrl?: string;
  metadata?: {
    eventId?: number;
    rankingChange?: number;
    achievementId?: string;
  };
}

export interface NotificationSettings {
  enabled: boolean;
  morningEnabled: boolean;
  morningTime: string; // "08:00" 形式
  eveningEnabled: boolean;
  eveningTime: string; // "21:00" 形式
  relationEnabled: boolean;
  eventEnabled: boolean;
  oshikatsuEnabled: boolean;
  anniversaryEnabled: boolean;
}

interface NotificationHistory {
  lastMorningNotification?: number;
  lastEveningNotification?: number;
  lastRelationNotification?: number;
  lastOshikatsuNotification?: number;
  sentNotificationIds: string[];
}

// ========== LocalStorage Keys ==========
const STORAGE_KEYS = {
  NOTIFICATIONS: 'ai_inochan_notifications',
  SETTINGS: 'ai_inochan_notification_settings',
  HISTORY: 'ai_inochan_notification_history',
};

// ========== デフォルト設定 ==========
const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: true,
  morningEnabled: true,
  morningTime: '08:00',
  eveningEnabled: true,
  eveningTime: '21:00',
  relationEnabled: true,
  eventEnabled: true,
  oshikatsuEnabled: true,
  anniversaryEnabled: true,
};

// ========== ユーティリティ関数 ==========

// 通知IDを生成
function generateNotificationId(type: NotificationType, content: string): string {
  const timestamp = Date.now();
  const hash = btoa(`${type}-${content}-${timestamp}`).substring(0, 16);
  return `notif_${hash}`;
}

// 今日の日付（YYYY-MM-DD）
function getTodayDateString(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

// 時刻が範囲内かチェック
function isTimeInRange(targetTime: string, rangeStart: string, rangeEnd: string): boolean {
  const target = parseInt(targetTime.replace(':', ''), 10);
  const start = parseInt(rangeStart.replace(':', ''), 10);
  const end = parseInt(rangeEnd.replace(':', ''), 10);
  return target >= start && target <= end;
}

// 最後の通知から指定時間経過したかチェック
function hasElapsedSince(lastTimestamp: number | undefined, hours: number): boolean {
  if (!lastTimestamp) return true;
  const elapsed = Date.now() - lastTimestamp;
  return elapsed >= hours * 60 * 60 * 1000;
}

// ========== カスタムフック ==========
export function useNotification() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);
  const [history, setHistory] = useState<NotificationHistory>({
    sentNotificationIds: [],
  });

  // ========== 初期化 ==========
  useEffect(() => {
    loadNotifications();
    loadSettings();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========== LocalStorage 読み込み ==========
  const loadNotifications = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.NOTIFICATIONS);
      if (stored) {
        const parsed = JSON.parse(stored) as Notification[];
        // 7日以上前の通知は削除
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const filtered = parsed.filter((n) => n.timestamp > sevenDaysAgo);
        setNotifications(filtered);
      }
    } catch (error) {
      console.error('通知読み込みエラー:', error);
    }
  }, []);

  const loadSettings = useCallback(() => {
    try {
      // 各設定を個別に読み込む
      const enabled = localStorage.getItem('ai_inochan_notification') !== 'false';
      const morningEnabled = localStorage.getItem('ai_inochan_morning_enabled') !== 'false';
      const morningTime = localStorage.getItem('ai_inochan_morning_time') || '08:00';
      const eveningEnabled = localStorage.getItem('ai_inochan_evening_enabled') !== 'false';
      const eveningTime = localStorage.getItem('ai_inochan_evening_time') || '21:00';
      const relationEnabled = localStorage.getItem('ai_inochan_relation_enabled') !== 'false';
      const eventEnabled = localStorage.getItem('ai_inochan_event_enabled') !== 'false';
      const oshikatsuEnabled = localStorage.getItem('ai_inochan_oshikatsu_enabled') !== 'false';
      const anniversaryEnabled = localStorage.getItem('ai_inochan_anniversary_enabled') !== 'false';

      setSettings({
        enabled,
        morningEnabled,
        morningTime,
        eveningEnabled,
        eveningTime,
        relationEnabled,
        eventEnabled,
        oshikatsuEnabled,
        anniversaryEnabled,
      });
    } catch (error) {
      console.error('通知設定読み込みエラー:', error);
    }
  }, []);

  const loadHistory = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.HISTORY);
      if (stored) {
        setHistory(JSON.parse(stored));
      }
    } catch (error) {
      console.error('通知履歴読み込みエラー:', error);
    }
  }, []);

  // ========== LocalStorage 保存 ==========
  const saveNotifications = useCallback((notifs: Notification[]) => {
    try {
      localStorage.setItem(STORAGE_KEYS.NOTIFICATIONS, JSON.stringify(notifs));
      setNotifications(notifs);
    } catch (error) {
      console.error('通知保存エラー:', error);
    }
  }, []);

  const saveSettings = useCallback((newSettings: NotificationSettings) => {
    try {
      localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(newSettings));
      setSettings(newSettings);
    } catch (error) {
      console.error('通知設定保存エラー:', error);
    }
  }, []);

  const saveHistory = useCallback((newHistory: NotificationHistory) => {
    try {
      localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(newHistory));
      setHistory(newHistory);
    } catch (error) {
      console.error('通知履歴保存エラー:', error);
    }
  }, []);

  // ========== 通知追加 ==========
  const addNotification = useCallback(
    (
      type: NotificationType,
      title: string,
      message: string,
      actionUrl?: string,
      metadata?: Notification['metadata']
    ): string | null => {
      // 通知が無効の場合は追加しない
      if (!settings.enabled) return null;

      // タイプ別の設定チェック
      if (type === 'habit' && !settings.morningEnabled && !settings.eveningEnabled) return null;
      if (type === 'relation' && !settings.relationEnabled) return null;
      if (type === 'event' && !settings.eventEnabled) return null;
      if (type === 'oshikatsu' && !settings.oshikatsuEnabled) return null;

      const id = generateNotificationId(type, message);

      // 重複チェック（同じIDの通知が既に存在する場合は追加しない）
      if (history.sentNotificationIds.includes(id)) {
        return null;
      }

      const newNotification: Notification = {
        id,
        type,
        title,
        message,
        timestamp: Date.now(),
        read: false,
        actionUrl,
        metadata,
      };

      const updated = [newNotification, ...notifications];
      saveNotifications(updated);

      // 履歴に追加
      const updatedHistory = {
        ...history,
        sentNotificationIds: [...history.sentNotificationIds, id],
      };
      saveHistory(updatedHistory);

      return id;
    },
    [settings, notifications, history, saveNotifications, saveHistory]
  );

  // ========== 通知既読 ==========
  const markAsRead = useCallback(
    (notificationId: string) => {
      const updated = notifications.map((n) =>
        n.id === notificationId ? { ...n, read: true } : n
      );
      saveNotifications(updated);
    },
    [notifications, saveNotifications]
  );

  // ========== 全て既読 ==========
  const markAllAsRead = useCallback(() => {
    const updated = notifications.map((n) => ({ ...n, read: true }));
    saveNotifications(updated);
  }, [notifications, saveNotifications]);

  // ========== 通知削除 ==========
  const deleteNotification = useCallback(
    (notificationId: string) => {
      const updated = notifications.filter((n) => n.id !== notificationId);
      saveNotifications(updated);
    },
    [notifications, saveNotifications]
  );

  // ========== 全て削除 ==========
  const clearAllNotifications = useCallback(() => {
    saveNotifications([]);
  }, [saveNotifications]);

  // ========== 未読数取得 ==========
  const getUnreadCount = useCallback((): number => {
    return notifications.filter((n) => !n.read).length;
  }, [notifications]);

  // ========== タイプ別通知取得 ==========
  const getNotificationsByType = useCallback(
    (type: NotificationType): Notification[] => {
      return notifications.filter((n) => n.type === type);
    },
    [notifications]
  );

  // ========== 通知頻度制御 ==========
  const canSendMorningNotification = useCallback((): boolean => {
    if (!settings.morningEnabled) return false;

    const today = getTodayDateString();
    const lastMorning = history.lastMorningNotification;
    if (lastMorning) {
      const lastDate = new Date(lastMorning).toISOString().split('T')[0];
      if (lastDate === today) return false;
    }

    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(
      now.getMinutes()
    ).padStart(2, '0')}`;

    const [targetHour] = settings.morningTime.split(':').map(Number);
    const startHour = Math.max(6, targetHour - 1);
    const endHour = Math.min(10, targetHour + 1);

    return isTimeInRange(currentTime, `${String(startHour).padStart(2, '0')}:00`, `${String(endHour).padStart(2, '0')}:59`);
  }, [settings, history]);

  const canSendEveningNotification = useCallback((): boolean => {
    if (!settings.eveningEnabled) return false;

    const today = getTodayDateString();
    const lastEvening = history.lastEveningNotification;
    if (lastEvening) {
      const lastDate = new Date(lastEvening).toISOString().split('T')[0];
      if (lastDate === today) return false;
    }

    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(
      now.getMinutes()
    ).padStart(2, '0')}`;

    const [targetHour] = settings.eveningTime.split(':').map(Number);
    const startHour = Math.max(20, targetHour - 1);
    const endHour = Math.min(23, targetHour + 1);

    return isTimeInRange(currentTime, `${String(startHour).padStart(2, '0')}:00`, `${String(endHour).padStart(2, '0')}:59`);
  }, [settings, history]);

  const canSendRelationNotification = useCallback((): boolean => {
    if (!settings.relationEnabled) return false;
    // 週1回まで（168時間 = 7日）
    return hasElapsedSince(history.lastRelationNotification, 168);
  }, [settings, history]);

  const canSendOshikatsuNotification = useCallback((): boolean => {
    if (!settings.oshikatsuEnabled) return false;
    // 1日1回まで（24時間）
    return hasElapsedSince(history.lastOshikatsuNotification, 24);
  }, [settings, history]);

  // ========== 通知送信履歴更新 ==========
  const updateMorningNotificationHistory = useCallback(() => {
    const updatedHistory = {
      ...history,
      lastMorningNotification: Date.now(),
    };
    saveHistory(updatedHistory);
  }, [history, saveHistory]);

  const updateEveningNotificationHistory = useCallback(() => {
    const updatedHistory = {
      ...history,
      lastEveningNotification: Date.now(),
    };
    saveHistory(updatedHistory);
  }, [history, saveHistory]);

  const updateRelationNotificationHistory = useCallback(() => {
    const updatedHistory = {
      ...history,
      lastRelationNotification: Date.now(),
    };
    saveHistory(updatedHistory);
  }, [history, saveHistory]);

  const updateOshikatsuNotificationHistory = useCallback(() => {
    const updatedHistory = {
      ...history,
      lastOshikatsuNotification: Date.now(),
    };
    saveHistory(updatedHistory);
  }, [history, saveHistory]);

  // ========== 設定更新 ==========
  const updateSettings = useCallback(
    (newSettings: Partial<NotificationSettings>) => {
      const updated = { ...settings, ...newSettings };
      saveSettings(updated);
    },
    [settings, saveSettings]
  );

  return {
    // 通知データ
    notifications,
    settings,
    history,

    // 通知操作
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,

    // 通知取得
    getUnreadCount,
    getNotificationsByType,

    // 頻度制御
    canSendMorningNotification,
    canSendEveningNotification,
    canSendRelationNotification,
    canSendOshikatsuNotification,

    // 履歴更新
    updateMorningNotificationHistory,
    updateEveningNotificationHistory,
    updateRelationNotificationHistory,
    updateOshikatsuNotificationHistory,

    // 設定更新
    updateSettings,
  };
}